import { useState, useCallback, TouchEvent } from 'react';

interface SwipeHandlers {
  onSwipeLeft?: () => void;
  onSwipeRight?: () => void;
  onSwipeUp?: () => void;
  onSwipeDown?: () => void;
}

interface SwipeResult {
  onTouchStart: (e: TouchEvent) => void;
  onTouchMove: (e: TouchEvent) => void;
  onTouchEnd: () => void;
  isSwiping: boolean;
  swipeDirection: 'left' | 'right' | 'up' | 'down' | null;
  swipeProgress: number; // 0-1
}

export function useSwipeGesture(
  handlers: SwipeHandlers,
  threshold: number = 50
): SwipeResult {
  const [touchStart, setTouchStart] = useState<{ x: number; y: number } | null>(null);
  const [touchCurrent, setTouchCurrent] = useState<{ x: number; y: number } | null>(null);
  const [isSwiping, setIsSwiping] = useState(false);

  const onTouchStart = useCallback((e: TouchEvent) => {
    const touch = e.touches[0];
    setTouchStart({ x: touch.clientX, y: touch.clientY });
    setTouchCurrent({ x: touch.clientX, y: touch.clientY });
    setIsSwiping(true);
  }, []);

  const onTouchMove = useCallback((e: TouchEvent) => {
    if (!touchStart) return;
    const touch = e.touches[0];
    setTouchCurrent({ x: touch.clientX, y: touch.clientY });
  }, [touchStart]);

  const onTouchEnd = useCallback(() => {
    if (!touchStart || !touchCurrent) {
      setIsSwiping(false);
      return;
    }

    const deltaX = touchCurrent.x - touchStart.x;
    const deltaY = touchCurrent.y - touchStart.y;
    const absX = Math.abs(deltaX);
    const absY = Math.abs(deltaY);

    if (Math.max(absX, absY) >= threshold) {
      if (absX > absY) {
        // Horizontal swipe
        if (deltaX > 0 && handlers.onSwipeRight) {
          handlers.onSwipeRight();
        } else if (deltaX < 0 && handlers.onSwipeLeft) {
          handlers.onSwipeLeft();
        }
      } else {
        // Vertical swipe
        if (deltaY > 0 && handlers.onSwipeDown) {
          handlers.onSwipeDown();
        } else if (deltaY < 0 && handlers.onSwipeUp) {
          handlers.onSwipeUp();
        }
      }
    }

    setTouchStart(null);
    setTouchCurrent(null);
    setIsSwiping(false);
  }, [touchStart, touchCurrent, threshold, handlers]);

  // Calculate swipe direction and progress
  let swipeDirection: 'left' | 'right' | 'up' | 'down' | null = null;
  let swipeProgress = 0;

  if (touchStart && touchCurrent) {
    const deltaX = touchCurrent.x - touchStart.x;
    const deltaY = touchCurrent.y - touchStart.y;
    const absX = Math.abs(deltaX);
    const absY = Math.abs(deltaY);

    if (absX > absY && absX > 10) {
      swipeDirection = deltaX > 0 ? 'right' : 'left';
      swipeProgress = Math.min(1, absX / threshold);
    } else if (absY > absX && absY > 10) {
      swipeDirection = deltaY > 0 ? 'down' : 'up';
      swipeProgress = Math.min(1, absY / threshold);
    }
  }

  return {
    onTouchStart,
    onTouchMove,
    onTouchEnd,
    isSwiping,
    swipeDirection,
    swipeProgress
  };
}




